import { LinkNode } from '@lexical/link';
import { ListItemNode, ListNode } from '@lexical/list';
import { HeadingNode } from '@lexical/rich-text';
import { VariableNode } from '@fluenceur/ui';
import {
  BULK_EMAIL_VARIABLES,
  INVITATION_OUTREACH_VARIABLES,
  TemplateChannel,
  templateContentMaxLength,
  templateContentWarnLength,
} from '@fluenceur/types';
import type { AvailableVariable } from '@fluenceur/ui';

// ── Editor theme ────────────────────────────────────────

export const EDITOR_THEME = {
  paragraph: 'editor-paragraph',
  text: {
    bold: 'editor-text-bold',
    italic: 'editor-text-italic',
    underline: 'editor-text-underline',
  },
  link: 'editor-link',
  heading: {
    h1: 'editor-heading-h1',
    h2: 'editor-heading-h2',
  },
  list: {
    ol: 'editor-list-ol',
    ul: 'editor-list-ul',
    listitem: 'editor-listitem',
  },
};

export const EDITOR_NODES = [VariableNode, LinkNode, ListNode, ListItemNode, HeadingNode];

export function handleLexicalError(error: Error) {
  console.error('Lexical error:', error);
}

export function createEditorConfig(namespace: string) {
  return {
    namespace,
    nodes: EDITOR_NODES,
    theme: EDITOR_THEME,
    onError: handleLexicalError,
  };
}

// ── Variable mapping ────────────────────────────────────

export const LEXICAL_VARIABLES: AvailableVariable[] = BULK_EMAIL_VARIABLES.map((key) => ({
  key,
  label: key,
  labelKey: `bulkEmail.modal.variables.${key}`,
  value: key,
}));

// The Fluenceur invitation templates also sign with the sender and state the
// affiliate benefit; the send job fills both for every invitation, so the
// composers offer them beside the outreach variables.
const INVITATION_TEMPLATE_EXTRA_VARIABLES = ['affiliate_benefit', 'sender_name'] as const;

export const INVITATION_OUTREACH_LEXICAL_VARIABLES: AvailableVariable[] = [
  ...INVITATION_OUTREACH_VARIABLES,
  ...INVITATION_TEMPLATE_EXTRA_VARIABLES,
].map((key) => ({
  key,
  label: key,
  labelKey: `invitationOutreach.variables.${key}`,
  value: key,
}));

// ── Limits ──────────────────────────────────────────────

export const MAX_BODY_LENGTH = templateContentMaxLength(TemplateChannel.EMAIL);
export const WARN_BODY_LENGTH = templateContentWarnLength(TemplateChannel.EMAIL);
export const MAX_FILES = 5;
export const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10MB
export const ACCEPTED_TYPES =
  '.pdf,.jpg,.jpeg,.png,.gif,.webp,.docx,.xlsx,.pptx,application/pdf,image/jpeg,image/png,image/gif,image/webp,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/vnd.openxmlformats-officedocument.presentationml.presentation';
